import { BulkResizeEditorOperation } from "@/store/features/bulkResize/types";
import { getCropBox } from "@/store/features/bulkResize/utils/getCropBox";
import { getFlipRotateData } from "@/store/features/bulkResize/utils/getFlipRotateData";
import { store } from "@/store/store";

const objectUrls: Record<string, string[]> = {};

export const useTransformImage = () => {
    const logObjectUrl = (imageId: string, url: string) => {
        const urls = objectUrls[imageId] || [];
        urls.push(url);
        objectUrls[imageId] = urls;
    };

    const cleanupOldObjectUrls = (imageId: string) => {
        const urls = objectUrls[imageId];
        const filtered = urls.length > 1 ? urls.slice(0, -1) : [];
        const image = store.getState().bulkResize.images.find((image) => image.id === imageId);
        filtered
            .filter((url) => url !== image?.original.src && url !== image?.preview.src)
            .forEach((url) => {
                URL.revokeObjectURL(url);
            });
        objectUrls[imageId] = [urls.at(-1)!];
    };

    const transformImage = async (imageId: string, operation: BulkResizeEditorOperation): Promise<string> => {
        const image = store.getState().bulkResize.images.find((image) => image.id === imageId);
        if (!image) throw new Error("Image not found in store during transform image");
        const operations = [...image.operations, operation];
        const crop = getCropBox(image, operations);
        let url = image.original.src;
        if (crop.width > 0 && crop.height > 0) {
            const worker = new Worker(new URL("./cropWorker.ts", import.meta.url));
            const { blob: result } = await runWorker(worker, {
                imageUrl: image.original.src,
                crop,
                title: image.title,
            });
             url = URL.createObjectURL(result);
            logObjectUrl(imageId, url);
        }

        const { flipH, flipV, rotation } = getFlipRotateData(operations);

        if (rotation !== 0) {
            const worker = new Worker(new URL("./rotateWorker.ts", import.meta.url));
            const { blob: result } = await runWorker(worker, {
                imageUrl: url,
                rotation,
            });
            url = URL.createObjectURL(result);
            logObjectUrl(imageId, url);
        }

        if (flipH || flipV) {
            const worker = new Worker(new URL("./flipWorker.ts", import.meta.url));
            const { blob: result } = await runWorker(worker, {
                imageUrl: url,
                flipH,
                flipV,
            });
            url = URL.createObjectURL(result);
            logObjectUrl(imageId, url);
        }

        setTimeout(() => cleanupOldObjectUrls(imageId), 500);

        return url;
    };

    return {
        transformImage,
    };
};

interface WorkerResponse {
    blob: Blob;
    title: string;
    messageId: string;
}

const runWorker = (worker: Worker, data: any): Promise<WorkerResponse> => {
    const messageId = crypto.randomUUID();

    return new Promise<WorkerResponse>((resolve, reject) => {


        const handleMessage = (e: MessageEvent) => {
            if (e.data.messageId !== messageId) return;
            cleanup();
            if (e.data.error) {
                reject(new Error(e.data.error));
            } else {
                resolve(e.data);
            }
        };

        const handleError = (e: ErrorEvent) => {
            cleanup();
            reject(new Error(e.message));
        };

        const cleanup = () => {
            worker.removeEventListener("message", handleMessage);
            worker.removeEventListener("error", handleError);
            worker.terminate();
        };

        worker.addEventListener("message", handleMessage);
        worker.addEventListener("error", handleError);

        try {
            worker.postMessage({ ...data, messageId });
        } catch (e) {
            cleanup();
            reject(e);
        }
    });
};
